import React, { useMemo, useRef, useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import StopIcon from '@mui/icons-material/Stop'

import usePrimaryColor from '../../../lib/hooks/usePrimaryColor'
import AssistantMessage from '../AssistantMessage'
import AssistantStepGroup from '../AssistantStepGroup'
import groupMessagesBySteps from './groupMessagesBySteps'
import { StreamingStatusAnnouncer, StreamingStatusIndicator } from '../StreamingStatusIndicator'
import { ChatMessage } from '../chatMessage'
import useTypewriter from './useTypewriter'
import useChatAutoScroll from './useChatAutoScroll'
import useSkillSlash from './useSkillSlash'
import AssistantChatInput, { AssistantChatInputValue } from '../AssistantChatInput'
import { SkillSlashMenu } from '../SkillSlashMenu'

export interface AssistantChatProps {
  messages: ChatMessage[]
  loading: boolean
  sendMessage: (text: string, skill?: string) => void
  stopGeneration: () => void
  inProgressText: string
  currentStatus: string | null
}

const EMPTY_INPUT: AssistantChatInputValue = { text: '', skill: null }
const STOP_RESPONDING_LABEL = 'Stop responding'

/**
 * Chat interface for the Civitas Learning AI assistant.
 *
 * Renders the message history, a chip-aware multiline input, an optional
 * floating slash-command menu for invoking known assistant skills, and a
 * Send button. The slash-menu state machine — caret tracking, query
 * detection, ranked filtering, keyboard navigation, combobox ARIA, and
 * unknown-skill auto-resend recovery — lives in `useSkillSlash`; this
 * component owns layout, message rendering, and send/stop orchestration.
 */
const AssistantChat = ({
  messages,
  loading,
  sendMessage,
  stopGeneration,
  inProgressText,
  currentStatus
}: AssistantChatProps) => {
  const color = usePrimaryColor()
  const [inputValue, setInputValue] = useState<AssistantChatInputValue>(EMPTY_INPUT)
  const inputRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const trimmedText = inputValue.text.trim()
  const displayedText = useTypewriter(inProgressText, loading)

  const skillSlash = useSkillSlash({
    inputValue,
    setInputValue,
    sendMessage,
    messages,
    inputRef
  })

  const { containerRef: messagesRef, onScroll: handleMessagesScroll } = useChatAutoScroll({
    messages,
    streamingText: displayedText
  })

  const canSend = !loading && (trimmedText.length > 0 || inputValue.skill !== null)

  const handleSend = () => {
    if (!canSend) return
    const text = trimmedText
    const { skill } = inputValue
    skillSlash.resetUnknownSkillGuard()
    setInputValue(EMPTY_INPUT)
    if (skill !== null) sendMessage(text, skill)
    else sendMessage(text)
  }

  const stopAndRestoreFocus = () => {
    stopGeneration()
    // Defer focus until React has flushed the loading=false update, so the
    // input stays focused when the Stop button unmounts and Send takes its slot.
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (skillSlash.handleKeyDown(e)) return

    if (e.key === 'Escape' && loading) {
      e.preventDefault()
      // Don't let MUI Drawer's Esc-to-close fire while the user is reading a
      // partial response.
      e.stopPropagation()
      stopAndRestoreFocus()
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const grouped = useMemo(() => groupMessagesBySteps(messages), [messages])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: 'grey.50'
      }}
    >
      <StreamingStatusAnnouncer status={currentStatus} />
      <Box
        ref={messagesRef}
        onScroll={handleMessagesScroll}
        role="log"
        aria-label="Chat messages"
        sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}
      >
        {grouped.map((group) =>
          group.type === 'steps' ? (
            <AssistantStepGroup key={group.key} steps={group.steps} />
          ) : (
            <AssistantMessage
              key={group.message.id}
              sender={group.message.sender}
              text={group.message.text}
              timestamp={group.message.timestamp}
              skill={group.message.skill}
            />
          )
        )}
        {loading && (
          <AssistantMessage
            sender="assistant"
            text={displayedText}
            streaming={inProgressText.length > 0}
          >
            <StreamingStatusIndicator label={currentStatus} />
          </AssistantMessage>
        )}
      </Box>
      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
        <Box
          ref={anchorRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 2,
            pr: 0.5,
            py: 0.75,
            borderRadius: 3,
            border: 1,
            borderColor: 'divider'
          }}
        >
          <AssistantChatInput
            ref={inputRef}
            value={inputValue}
            onChange={skillSlash.handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything…"
            comboboxProps={skillSlash.comboboxProps}
          />
          {loading ? (
            <Tooltip title={STOP_RESPONDING_LABEL}>
              <IconButton
                aria-label={STOP_RESPONDING_LABEL}
                onClick={stopAndRestoreFocus}
                size="small"
                sx={{ color }}
              >
                <StopIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              aria-label="Send"
              onClick={handleSend}
              disabled={!canSend}
              size="small"
              sx={{ color }}
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
        <SkillSlashMenu
          rankedSkills={skillSlash.filtered}
          activeIndex={skillSlash.activeIndex}
          onActiveIndexChange={skillSlash.setActiveIndex}
          onSelect={skillSlash.selectSkill}
          onClose={skillSlash.closeMenu}
          anchorEl={skillSlash.menuOpen ? anchorRef.current : null}
          loading={skillSlash.skillsLoading}
          error={skillSlash.skillsError}
        />
      </Box>
    </Box>
  )
}

export default AssistantChat
